import { BfDevice } from "../api/models";
import { BfMessageFcmData } from "../api/models/bf-message-fcm-data";
import { isCapacitor, isiOSApp, isWebKit } from "../client";

export enum FirebaseInstanceType {
    Unsupported,
    App,
    Capacitor,
    ServiceWorker,
}

export interface MessagingOptions {
    apiKey: string;
    projectId: string;
    messagingSenderId: string;
    appId: string;
}

export const showNotificationWithServiceWorker = (
    fcmMessage: BfMessageFcmData,
    swRegistration: ServiceWorkerRegistration
) => {
    const notification: NotificationOptions = {
        body: fcmMessage.body,
        icon: fcmMessage.icon,
        badge: fcmMessage.badge,
        data: fcmMessage,
    };

    return swRegistration.showNotification(fcmMessage.title, notification);
};

export const isNotificationTypeSupported = () => {
    // Check if notification is present
    if (!("Notification" in window) || !Notification.requestPermission) {
        return false;
    }

    return true;
};

// From Firebase code to avoid causing a throw
const supportsFirebase = () => {
    return (
        typeof window != "undefined" &&
        typeof indexedDB == "object" &&
        !(typeof navigator == "undefined" || !navigator.cookieEnabled) &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window &&
        "fetch" in window &&
        // eslint-disable-next-line no-prototype-builtins
        ServiceWorkerRegistration.prototype.hasOwnProperty("showNotification") &&
        // eslint-disable-next-line no-prototype-builtins
        PushSubscription.prototype.hasOwnProperty("getKey")
    );
};

export const supportsFirebaseMessaging = (device: BfDevice) => {
    // Supports iOS via Cordova, Android, and PC

    // Safari is not supported by Firebase
    if (isWebKit() && !isiOSApp()) return false;

    // Capacitor is supported
    if (isCapacitor()) {
        return true;
    }

    // Edge on Android doesn't support background notifications
    // https://github.com/bushfireio/ux/issues/85
    // Waiting for Chrome 77+ on android
    if (
        device.useragent &&
        device.useragent.includes("Android") &&
        device.useragent.includes("Chrome") &&
        // Android Edge 46 (Chrome 77) doesn't support background notifications
        // Android Edge 93 (Chrome 93) doesn't support background notifications
        (device.useragent.includes("EdgA/46") || device.useragent.includes("EdgA/93"))
    ) {
        return false;
    }

    // Run the same firebase check as the firebase code
    if (!supportsFirebase()) return false;

    // Double check we can show the notification
    if (!isNotificationTypeSupported()) {
        // PC and Android, when running in fg
        return false;
    }

    return true;
};
